/**
 * Activity-Based Token Refresh System
 *
 * Industry Standard Implementation:
 * - JWT tokens are short-lived (8h) for security
 * - Tokens are automatically refreshed when user is ACTIVE
 * - Activity = clicks, key presses, mouse movement, scrolling, form input
 * - Inactive users are logged out after session timeout (configurable)
 *
 * This prevents users from being logged out while actively working
 * (e.g., writing clinical notes) - a critical UX requirement for healthcare apps.
 *
 * CRITICAL FIX (2024): Now uses SHARED activity timestamp from InactivityMonitor
 * to ensure both systems agree on user activity status. Previously, this component
 * had its own 15-minute activity timeout that would stop token refresh even while
 * users were actively typing in forms.
 */

import { useEffect, useRef, useCallback } from 'react';
import { getTokenInfo, refreshAuthToken, isAuthenticated } from '../utils/tokenUtils';

import { safeStorage } from '../utils/safeStorage';
// Configuration
const CONFIG = {
  // How often to check if token needs refresh (in milliseconds)
  CHECK_INTERVAL: 60 * 1000, // 1 minute

  // Refresh token when it expires within this time (in seconds)
  REFRESH_THRESHOLD: 30 * 60, // 30 minutes before expiry

  // Minimum time between refresh attempts (prevent hammering)
  MIN_REFRESH_INTERVAL: 5 * 60 * 1000, // 5 minutes

  // Activity timeout - MUST MATCH InactivityMonitor
  // Development: 60 minutes, Production: 30 minutes
  ACTIVITY_TIMEOUT: process.env.NODE_ENV === 'development'
    ? 60 * 60 * 1000   // 60 minutes in dev
    : 30 * 60 * 1000,  // 30 minutes in production

  // Debounce activity tracking (don't track every tiny movement)
  ACTIVITY_DEBOUNCE: 500, // 500ms (reduced from 1s for better responsiveness)

  // Shared storage key - MUST MATCH InactivityMonitor
  ACTIVITY_STORAGE_KEY: 'evv_last_activity_timestamp',
};

const ActivityBasedTokenRefresh = () => {
  // Track last refresh attempt
  const lastRefreshRef = useRef(0);
  const intervalRef = useRef(null);
  const activityDebounceRef = useRef(null);

  /**
   * Update activity timestamp in SHARED localStorage
   * CRITICAL: Uses same key as InactivityMonitor for consistent activity tracking
   * This ensures both systems agree on user activity status
   */
  const updateActivity = useCallback(() => {
    // Debounce activity updates
    if (activityDebounceRef.current) {
      clearTimeout(activityDebounceRef.current);
    }

    activityDebounceRef.current = setTimeout(() => {
      const now = Date.now();
      // Write to SHARED localStorage key used by InactivityMonitor
      safeStorage.setItem(CONFIG.ACTIVITY_STORAGE_KEY, now.toString());
    }, CONFIG.ACTIVITY_DEBOUNCE);
  }, []);

  /**
   * Check if user is currently active using SHARED activity timestamp
   * CRITICAL FIX: Now reads from the same localStorage key that InactivityMonitor writes to
   * This ensures token refresh continues while user is actively typing in forms
   */
  const isUserActive = useCallback(() => {
    const now = Date.now();
    // Read from SHARED localStorage key - same as InactivityMonitor
    const lastActivity = parseInt(safeStorage.getItem(CONFIG.ACTIVITY_STORAGE_KEY) || '0', 10);
    const timeSinceActivity = now - lastActivity;

    const isActive = timeSinceActivity < CONFIG.ACTIVITY_TIMEOUT;

    if (!isActive) {
      console.log(`🔄 Activity check: inactive for ${Math.floor(timeSinceActivity / 60000)} minutes`);
    }

    return isActive;
  }, []);

  /**
   * Attempt to refresh token if needed
   * CRITICAL: This runs every minute to ensure token stays fresh for active users
   */
  const checkAndRefreshToken = useCallback(async () => {
    // Don't run if not authenticated
    if (!isAuthenticated()) {
      return;
    }

    // Check if user is active using SHARED activity timestamp
    if (!isUserActive()) {
      // User is inactive - don't refresh token
      // InactivityMonitor will handle logout after 60 minutes of inactivity
      return;
    }

    // Get current token info
    const tokenInfo = getTokenInfo();
    if (!tokenInfo.exists || tokenInfo.expired) {
      console.log('🔄 Token expired or missing - cannot refresh');
      return;
    }

    const timeUntilExpiry = tokenInfo.timeUntilExpiry;
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshRef.current;

    /**
     * Refresh conditions:
     * 1. Token expires within REFRESH_THRESHOLD (30 min)
     * 2. Haven't refreshed in the last MIN_REFRESH_INTERVAL (5 min)
     *
     * CRITICAL: We ALWAYS refresh for active users when threshold is reached
     * This ensures users writing long clinical notes never get logged out
     */
    if (timeUntilExpiry <= CONFIG.REFRESH_THRESHOLD &&
        timeSinceLastRefresh >= CONFIG.MIN_REFRESH_INTERVAL) {

      console.log(`🔄 Auto-refreshing token for active user (expires in ${Math.floor(timeUntilExpiry / 60)} min)`);

      try {
        const success = await refreshAuthToken();

        if (success) {
          lastRefreshRef.current = now;
          console.log('✅ Token auto-refreshed successfully - session extended');
        } else {
          console.warn('⚠️ Token auto-refresh failed, will retry in 1 minute');
        }
      } catch (error) {
        console.error('❌ Token auto-refresh error:', error.message);
      }
    }
  }, [isUserActive]);

  // Set up activity listeners and refresh interval
  useEffect(() => {
    if (!isAuthenticated()) {
      return;
    }

    /**
     * COMPREHENSIVE activity events list
     * CRITICAL: Must match InactivityMonitor for consistent activity detection
     * Using capture phase (true) ensures we catch events before they're stopped
     */
    const activityEvents = [
      // Mouse events
      'mousedown',
      'mousemove',
      'mouseup',
      'click',
      'dblclick',
      'wheel',
      // Keyboard events - CRITICAL for detecting typing
      'keydown',
      'keypress',
      'keyup',
      // Touch events (mobile/tablet)
      'touchstart',
      'touchmove',
      'touchend',
      // Form input events - CRITICAL for clinical note-taking
      'input',           // Fires when input value changes
      'change',          // Fires when select/checkbox changes
      'focus',           // User clicked into a field
      'blur',            // User left a field
      // Pointer events
      'pointerdown',
      'pointermove',
      // Scroll events
      'scroll',
      // Clipboard events - important for copy/paste in notes
      'paste',
      'cut',
      'copy',
    ];

    /**
     * Add activity listeners with CAPTURE phase
     * Capture phase (true) ensures we detect events even if stopped by other handlers
     */
    activityEvents.forEach(event => {
      // Use document instead of window for better form event capture
      document.addEventListener(event, updateActivity, { passive: true, capture: true });
    });

    // Handle visibility change specially (tab switching)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateActivity();
        // Immediately check token when user comes back to tab
        checkAndRefreshToken();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initialize shared activity timestamp if not exists
    if (!safeStorage.getItem(CONFIG.ACTIVITY_STORAGE_KEY)) {
      safeStorage.setItem(CONFIG.ACTIVITY_STORAGE_KEY, Date.now().toString());
    }

    // Start periodic check
    intervalRef.current = setInterval(checkAndRefreshToken, CONFIG.CHECK_INTERVAL);

    // Initial check
    checkAndRefreshToken();

    console.log('🛡️ Activity-based token refresh initialized (using shared activity tracking)');

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateActivity, { capture: true });
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (activityDebounceRef.current) {
        clearTimeout(activityDebounceRef.current);
      }
    };
  }, [updateActivity, checkAndRefreshToken]);

  // This component doesn't render anything visible
  return null;
};

export default ActivityBasedTokenRefresh;
